import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, mergeProps as _mergeProps, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { key: 1 }
const _hoisted_3 = { class: "d-flex align-center" }
const _hoisted_4 = { class: "d-flex align-center" }
const _hoisted_5 = { class: "d-flex align-center" }
const _hoisted_6 = { class: "d-flex align-center" }
const _hoisted_7 = { class: "d-flex justify-space-between align-center mb-1" }
const _hoisted_8 = { class: "text-subtitle-2" }
const _hoisted_9 = { class: "d-flex justify-space-between align-center mb-1" }
const _hoisted_10 = { class: "text-subtitle-2" }
const _hoisted_11 = { class: "d-flex justify-space-between align-center" }
const _hoisted_12 = { class: "text-subtitle-1" }
const _hoisted_13 = { class: "d-flex align-center" }
const _hoisted_14 = { class: "text-subtitle-2" }
const _hoisted_15 = { class: "text-caption" }

import { ref, onMounted, getCurrentInstance, computed } from 'vue';
import { useSalesStore } from '@/store/sales';
import { useFloatFormStore } from '@/store/floatForm';
import { useBottomSheetStore } from '@/store/bottomSheet';
import { useRoute } from 'vue-router';
import { loadModuleConfig, resolveViewName, getListPreset } from '@/lib/presets';

type ActionMeta = {
  label: string;
  onClick: string;
  icon?: string;
  permission?: string;
  kind?: 'default'|'extra';
  showIf?: (item:any)=>boolean;
  showWhen?: string;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'Detail',
  props: {
    id: {},
    moduleName: { default: 'Sales' },
    title: {},
    rowActionHandlers: {}
  },
  emits: ["close", "refresh"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const store = useSalesStore();
const floatFormStore = useFloatFormStore();
const bottomSheetStore = useBottomSheetStore();
const route = useRoute();

const sale = ref<any>(null);
const loading = ref(true);

// ====== helpers UI ======
const fmtCurrency = (v: any) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 }).format(Number(v || 0));
const formatDate = (v?: string) => (v ? new Date(v).toLocaleString('es-MX') : '—');
const clientName = (c: any) => c?.attributes?.commercial_name || c?.attributes?.legal_name || c?.attributes?.name || '—';
const userFullName = (u: any) => [u?.name, u?.last_name ?? u?.last].filter(Boolean).join(' ') || '—';
const toUrl = (v: any) => (typeof v === 'string' ? v : (v?.url || v?.path || v?.file));

// ====== tabla de items ======
const itemHeaders = [
  { title: 'Producto', value: 'product' },
  { title: 'Cantidad', value: 'quantity' },
  { title: 'Precio unitario', value: 'unit_price' },
  { title: 'Total', value: 'price_with_iva' },
  { title: 'Autorización', value: 'needs_authorization' },
];
const items = computed(() => sale.value?.sale_items || []);

// ====== acciones (mismo origen que la lista) ======
const actions = ref<ActionMeta[]>([]);
const instance = getCurrentInstance();
const $can = instance?.appContext.config.globalProperties.$can as (perm: string, item?: any) => boolean;

function byPath(obj:any, path?:string){
  if (!obj || !path) return undefined;
  return path.split('.').reduce((a,k)=> (a ? a[k] : undefined), obj);
}
function passesShow(a:any, item:any){
  if (typeof a?.showIf === 'function') return !!a.showIf(item);
  if (a?.showWhen) return !!byPath(item, a.showWhen);
  return true;
}

onMounted(async () => {
  try {
    loading.value = true;
    sale.value = await store.fetchSaleById(props.id);

    // carga preset de acciones
    const mod = await loadModuleConfig(props.moduleName);
    const view = resolveViewName(props.moduleName, route, mod);
    const preset = getListPreset(mod, view);
    const raw = (preset.rowActions || (mod as any).ROW_ACTIONS || []) as ActionMeta[];

    actions.value = raw
      .filter(a => !a.permission || ($can && $can(a.permission, sale.value)))
      .filter(a => passesShow(a, sale.value));
  } catch (e) {
    console.error('[Sales Detail] error:', e);
  } finally {
    loading.value = false;
  }
});

// Handlers coherentes con lista
function editItem() {
  floatFormStore.openForm({
    component: 'EditForm',
    formTitle: 'Editar Venta',
    formWidth: '1080',
    module: props.moduleName,
    props: {
      item: sale.value,
      onUpdate: async (data:any) => {
        await store.updateSale(data.id, data);
        emit('refresh');
      },
    },
  });
}
function deleteItem() {
  bottomSheetStore.openSheet({
    module: props.moduleName,
    formTitle: `¿Seguro(a) que deseas eliminar Venta ${sale.value?.id}?`,
    props: {
      item: sale.value,
      message: 'Esta acción no se puede deshacer.',
      action: async () => {
        await store.deleteSale(sale.value?.id);
        emit('refresh');
        emit('close');
      },
    },
  });
}

async function onAction(a: ActionMeta) {
  if (!sale.value) return;

  if (a.onClick === '__edit') return editItem();
  if (a.onClick === '__delete') return deleteItem();

  // custom desde Sales.vue (authorizeSale, confirmSale, etc.)
  const handlers = props.rowActionHandlers || {};
  if (handlers[a.onClick]) {
    await handlers[a.onClick](sale.value);
    emit('refresh');
    return;
  }

  console.warn('No handler for action:', a.onClick);
}

return (_ctx: any,_cache: any) => {
  const _component_v_toolbar_title = _resolveComponent("v-toolbar-title")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_list_item_title = _resolveComponent("v-list-item-title")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_list = _resolveComponent("v-list")!
  const _component_v_menu = _resolveComponent("v-menu")!
  const _component_v_toolbar = _resolveComponent("v-toolbar")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_data_table = _resolveComponent("v-data-table")!

  return (_openBlock(), _createBlock(_component_v_card, null, {
    default: _withCtx(() => [
      _createVNode(_component_v_toolbar, {
        density: "comfortable",
        flat: ""
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_toolbar_title, null, {
            default: _withCtx(() => [
              _createTextVNode(" Venta #" + _toDisplayString(_ctx.id), 1)
            ]),
            _: 1
          }),
          _createVNode(_component_v_spacer),
          (actions.value.length)
            ? (_openBlock(), _createBlock(_component_v_menu, {
                key: 0,
                location: "bottom end"
              }, {
                activator: _withCtx(({ props }) => [
                  _createVNode(_component_v_btn, _mergeProps(props, {
                    icon: "",
                    variant: "text"
                  }), {
                    default: _withCtx(() => [
                      _createVNode(_component_v_icon, null, {
                        default: _withCtx(() => _cache[1] || (_cache[1] = [
                          _createTextVNode("mdi-dots-vertical")
                        ])),
                        _: 1,
                        __: [1]
                      })
                    ]),
                    _: 2
                  }, 1040)
                ]),
                default: _withCtx(() => [
                  _createVNode(_component_v_list, null, {
                    default: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(actions.value, (a) => {
                        return (_openBlock(), _createBlock(_component_v_list_item, {
                          key: a.onClick,
                          onClick: ($event: any) => (onAction(a))
                        }, {
                          prepend: _withCtx(() => [
                            (a.icon)
                              ? (_openBlock(), _createBlock(_component_v_icon, {
                                  key: 0,
                                  class: "mr-2",
                                  size: "small"
                                }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(a.icon), 1)
                                  ]),
                                  _: 2
                                }, 1024))
                              : _createCommentVNode("", true)
                          ]),
                          default: _withCtx(() => [
                            _createVNode(_component_v_list_item_title, null, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(a.label), 1)
                              ]),
                              _: 2
                            }, 1024)
                          ]),
                          _: 2
                        }, 1032, ["onClick"]))
                      }), 128))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          _createVNode(_component_v_btn, {
            icon: "",
            variant: "text",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('close')))
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_icon, null, {
                default: _withCtx(() => _cache[2] || (_cache[2] = [
                  _createTextVNode("mdi-close")
                ])),
                _: 1,
                __: [2]
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_component_v_divider),
      _createVNode(_component_v_card_text, { class: "py-4" }, {
        default: _withCtx(() => [
          (loading.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createVNode(_component_v_skeleton_loader, { type: "article, table" })
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_2, [
                _createVNode(_component_v_row, {
                  dense: "",
                  class: "mb-2"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_col, {
                      cols: "12",
                      md: "7"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_row, { dense: "" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_col, {
                              cols: "12",
                              class: "pb-1"
                            }, {
                              default: _withCtx(() => [
                                _createElementVNode("div", _hoisted_3, [
                                  _createVNode(_component_v_icon, {
                                    size: "18",
                                    class: "mr-2"
                                  }, {
                                    default: _withCtx(() => _cache[3] || (_cache[3] = [
                                      _createTextVNode("mdi-domain")
                                    ])),
                                    _: 1,
                                    __: [3]
                                  }),
                                  _cache[4] || (_cache[4] = _createElementVNode("strong", null, "Cliente: ", -1)),
                                  _createElementVNode("span", null, _toDisplayString(clientName(sale.value?.client)), 1)
                                ])
                              ]),
                              _: 1
                            }),
                            (sale.value?.campaign)
                              ? (_openBlock(), _createBlock(_component_v_col, {
                                  key: 0,
                                  cols: "12",
                                  class: "pb-1"
                                }, {
                                  default: _withCtx(() => [
                                    _createElementVNode("div", _hoisted_4, [
                                      _createVNode(_component_v_icon, {
                                        size: "18",
                                        class: "mr-2"
                                      }, {
                                        default: _withCtx(() => _cache[5] || (_cache[5] = [
                                          _createTextVNode("mdi-bullhorn")
                                        ])),
                                        _: 1,
                                        __: [5]
                                      }),
                                      _cache[6] || (_cache[6] = _createElementVNode("strong", null, "Campaña: ", -1)),
                                      _createElementVNode("span", null, _toDisplayString(sale.value?.campaign), 1)
                                    ])
                                  ]),
                                  _: 1
                                }))
                              : _createCommentVNode("", true),
                            _createVNode(_component_v_col, {
                              cols: "12",
                              class: "pb-1"
                            }, {
                              default: _withCtx(() => [
                                _createElementVNode("div", _hoisted_5, [
                                  _createVNode(_component_v_icon, {
                                    size: "18",
                                    class: "mr-2"
                                  }, {
                                    default: _withCtx(() => _cache[7] || (_cache[7] = [
                                      _createTextVNode("mdi-account")
                                    ])),
                                    _: 1,
                                    __: [7]
                                  }),
                                  _cache[8] || (_cache[8] = _createElementVNode("strong", null, "Vendedor: ", -1)),
                                  _createElementVNode("span", null, _toDisplayString(userFullName(sale.value?.user)), 1)
                                ])
                              ]),
                              _: 1
                            }),
                            _createVNode(_component_v_col, {
                              cols: "12",
                              class: "pb-1"
                            }, {
                              default: _withCtx(() => [
                                _createElementVNode("div", _hoisted_6, [
                                  _createVNode(_component_v_icon, {
                                    size: "18",
                                    class: "mr-2"
                                  }, {
                                    default: _withCtx(() => _cache[9] || (_cache[9] = [
                                      _createTextVNode("mdi-calendar")
                                    ])),
                                    _: 1,
                                    __: [9]
                                  }),
                                  _cache[10] || (_cache[10] = _createElementVNode("strong", null, "Creación: ", -1)),
                                  _createElementVNode("span", null, _toDisplayString(formatDate(sale.value?.created_at)), 1)
                                ])
                              ]),
                              _: 1
                            }),
                            (!!sale.value?.needs_authorization)
                              ? (_openBlock(), _createBlock(_component_v_col, {
                                  key: 1,
                                  cols: "12",
                                  class: "pb-1"
                                }, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_v_chip, {
                                      color: "orange",
                                      size: "small",
                                      label: ""
                                    }, {
                                      default: _withCtx(() => _cache[11] || (_cache[11] = [
                                        _createTextVNode(" Requiere Autorización ")
                                      ])),
                                      _: 1,
                                      __: [11]
                                    })
                                  ]),
                                  _: 1
                                }))
                              : _createCommentVNode("", true)
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_v_col, {
                      cols: "12",
                      md: "5"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_card, { variant: "tonal" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_card_text, { class: "py-3" }, {
                              default: _withCtx(() => [
                                _createElementVNode("div", _hoisted_7, [
                                  _cache[12] || (_cache[12] = _createElementVNode("div", { class: "text-caption" }, "Subtotal", -1)),
                                  _createElementVNode("div", _hoisted_8, _toDisplayString(fmtCurrency(sale.value?.subtotal)), 1)
                                ]),
                                _createElementVNode("div", _hoisted_9, [
                                  _cache[13] || (_cache[13] = _createElementVNode("div", { class: "text-caption" }, "IVA", -1)),
                                  _createElementVNode("div", _hoisted_10, _toDisplayString(fmtCurrency(sale.value?.iva)), 1)
                                ]),
                                _createVNode(_component_v_divider, { class: "my-2" }),
                                _createElementVNode("div", _hoisted_11, [
                                  _cache[14] || (_cache[14] = _createElementVNode("div", { class: "text-subtitle-2" }, [
                                    _createElementVNode("strong", null, "Total")
                                  ], -1)),
                                  _createElementVNode("div", _hoisted_12, [
                                    _createElementVNode("strong", null, _toDisplayString(fmtCurrency(sale.value?.total)), 1)
                                  ])
                                ])
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_card, {
                  class: "mt-3",
                  variant: "elevated"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_card_title, { class: "py-3" }, {
                      default: _withCtx(() => _cache[15] || (_cache[15] = [
                        _createTextVNode("Productos")
                      ])),
                      _: 1,
                      __: [15]
                    }),
                    _createVNode(_component_v_divider),
                    _createVNode(_component_v_data_table, {
                      headers: itemHeaders,
                      items: items.value,
                      density: "comfortable",
                      "items-per-page": 10
                    }, {
                      "item.product": _withCtx(({ item }) => [
                        _createTextVNode(_toDisplayString(item?.sold_item?.attributes?.description || item?.description || '—'), 1)
                      ]),
                      "item.quantity": _withCtx(({ item }) => [
                        _createTextVNode(_toDisplayString(item?.quantity ?? '—'), 1)
                      ]),
                      "item.unit_price": _withCtx(({ item }) => [
                        _createTextVNode(_toDisplayString(fmtCurrency(item?.unit_price)), 1)
                      ]),
                      "item.price_with_iva": _withCtx(({ item }) => [
                        _createTextVNode(_toDisplayString(fmtCurrency(item?.price_with_iva)), 1)
                      ]),
                      "item.needs_authorization": _withCtx(({ item }) => [
                        (item?.needs_authorization)
                          ? (_openBlock(), _createBlock(_component_v_chip, {
                              key: 0,
                              color: "orange",
                              size: "x-small",
                              label: ""
                            }, {
                              default: _withCtx(() => _cache[16] || (_cache[16] = [
                                _createTextVNode("Autorizar")
                              ])),
                              _: 1,
                              __: [16]
                            }))
                          : _createCommentVNode("", true)
                      ]),
                      _: 1
                    }, 8, ["items"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_row, {
                  dense: "",
                  class: "mt-3"
                }, {
                  default: _withCtx(() => [
                    (sale.value?.pdf_document)
                      ? (_openBlock(), _createBlock(_component_v_col, {
                          key: 0,
                          cols: "12",
                          md: "6"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_card, { variant: "tonal" }, {
                              default: _withCtx(() => [
                                _createVNode(_component_v_card_text, null, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_v_btn, {
                                      href: toUrl(sale.value?.pdf_document),
                                      target: "_blank",
                                      rel: "noopener",
                                      "prepend-icon": "mdi-open-in-new",
                                      variant: "text"
                                    }, {
                                      default: _withCtx(() => _cache[17] || (_cache[17] = [
                                        _createTextVNode(" Ver PDF de la venta ")
                                      ])),
                                      _: 1,
                                      __: [17]
                                    }, 8, ["href"])
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        }))
                      : _createCommentVNode("", true),
                    (sale.value?.quotation)
                      ? (_openBlock(), _createBlock(_component_v_col, {
                          key: 1,
                          cols: "12",
                          md: "6"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_card, { variant: "tonal" }, {
                              default: _withCtx(() => [
                                _createVNode(_component_v_card_text, null, {
                                  default: _withCtx(() => [
                                    _createElementVNode("div", _hoisted_13, [
                                      _createVNode(_component_v_icon, { class: "mr-2" }, {
                                        default: _withCtx(() => _cache[18] || (_cache[18] = [
                                          _createTextVNode("mdi-file-document")
                                        ])),
                                        _: 1,
                                        __: [18]
                                      }),
                                      _createElementVNode("div", null, [
                                        _createElementVNode("div", _hoisted_14, "Cotización #" + _toDisplayString(sale.value.quotation.id), 1),
                                        _createElementVNode("div", _hoisted_15, "Campaña: " + _toDisplayString(sale.value.quotation.campaign || '—'), 1)
                                      ])
                                    ])
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        }))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                })
              ]))
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})